import React, { useCallback, useMemo, useState } from 'react';
import Joi from 'joi';

import { useGetTunnelMessagesQuery, useUpdateDistributorCompanyMutation } from 'api/main';
import { EURO_SYMBOL } from 'utils/constants';
import { parseSum, purgeEmptyFields, validateSchema } from 'utils/helpers';
import { texts } from 'utils/languages/fr';

import InfoRow from './InfoRow';
import InfoBox from './InfoBox';
import SectionActions from './SectionActions';
import { CustomTextField, ErrorMessage, RadioLabel } from 'components';
import { Box, FormControlLabel, InputAdornment, RadioGroup, Typography } from '@mui/material';

// TODO: move this and use it as a constant everywhere
const radioKeys = [
    'asset_management_amount_options.between_0_and_50ME',
    'asset_management_amount_options.between_50_and_100ME',
    'asset_management_amount_options.more_than_100ME',
];

const formControlSx = {
    marginLeft: 0,
    display: 'flex',
    alignItems: 'center',
    marginBottom: 2,
};

const percentageInputSx = { width: '80px' };

const inputSx = { marginBottom: 0 };

const getDefaultForm = () => ({
    assetManagementAmount: '',
    privateEquityAssetManagementPercentage: '',
    professionalClientPercentage: '',
    informedClientPercentage: '',
    nonProfessionalClientPercentage: '',
    distributionWays: '',
    averagePrivateEquityTicketAmount: '',
});

const schema = {
    distributionWays: Joi.string()
        .allow('')
        .max(250)
        .messages({ 'string.max': texts.TEXT_TOO_LONG() }),
    privateEquityAssetManagementPercentage: Joi.number().allow('').min(0).max(100).messages({
        'number.base': texts.PERCENTAGE_VALUE,
        'number.min': texts.PERCENTAGE_VALUE,
        'number.max': texts.PERCENTAGE_VALUE,
    }),
    professionalClientPercentage: Joi.number().allow('').min(0).max(100).messages({
        'number.base': texts.PERCENTAGE_VALUE,
        'number.min': texts.PERCENTAGE_VALUE,
        'number.max': texts.PERCENTAGE_VALUE,
    }),
    informedClientPercentage: Joi.number().allow('').min(0).max(100).messages({
        'number.base': texts.PERCENTAGE_VALUE,
        'number.min': texts.PERCENTAGE_VALUE,
        'number.max': texts.PERCENTAGE_VALUE,
    }),
    nonProfessionalClientPercentage: Joi.number().allow('').min(0).max(100).messages({
        'number.base': texts.PERCENTAGE_VALUE,
        'number.min': texts.PERCENTAGE_VALUE,
        'number.max': texts.PERCENTAGE_VALUE,
    }),
};

const ComplementsSesction = props => {
    const { distributor, canEditMissingInfo } = props;

    const { data: messages } = useGetTunnelMessagesQuery();
    const [form, setForm] = useState(getDefaultForm());
    const [errors, setErrors] = useState({
        assetManagementAmount: '',
        privateEquityAssetManagementPercentage: '',
        totalPercent: '',
    });

    const [updateDistributorCompany, { isLoading: loading }] =
        useUpdateDistributorCompanyMutation();

    const clientRepartition = [
        `${distributor?.professionalClientPercentage || '-'}% de clients professionnels`,
        `${distributor?.informedClientPercentage || '-'}% de clients avertis`,
        `${distributor?.nonProfessionalClientPercentage || '-'}% de clients non professionnels`,
    ];

    const hasClientRepartition =
        distributor?.professionalClientPercentage ||
        distributor?.informedClientPercentage ||
        distributor?.nonProfessionalClientPercentage;

    const assetManagementAmount = useMemo(() => {
        if (!messages || !distributor || !distributor.assetManagementAmount) return '-';
        return messages[distributor.assetManagementAmount] || '-';
    }, [messages, distributor]);

    const remunerationMethod = useMemo(() => {
        if (
            distributor?.publicCategoryCommercialization &&
            distributor?.privateCategoryCommercialization
        )
            return texts.BOTH;
        if (distributor?.publicCategoryCommercialization) return texts.ACCEPT_REMUNERATION;
        return texts.REJECT_REMUNERATION;
    }, [distributor]);

    const handleChange = useCallback(
        key => e => setForm(prevState => ({ ...prevState, [key]: e.target.value })),
        []
    );
    const handleCancel = useCallback(() => setForm(getDefaultForm()), []);
    const handleSubmit = useCallback(() => {
        const percentageFieldsEmpty =
            form.nonProfessionalClientPercentage === '' &&
            form.informedClientPercentage === '' &&
            form.professionalClientPercentage === '';
        const totalPercentages =
            +form.nonProfessionalClientPercentage +
            +form.informedClientPercentage +
            +form.professionalClientPercentage;
        const { valid, newErrors } = validateSchema(schema, form);
        setErrors(newErrors);

        if (!percentageFieldsEmpty) {
            if (totalPercentages !== 100) {
                setErrors({ ...newErrors, totalPercent: texts.TOTAL_DES_POURCENTAGES });
                return false;
            }
        }

        if (valid) {
            const purgedForm = purgeEmptyFields(form);

            updateDistributorCompany({
                payload: {
                    id: distributor.id,
                    ...purgedForm,
                    privateEquityAssetManagementPercentage:
                        +purgedForm.privateEquityAssetManagementPercentage || undefined,
                    averagePrivateEquityTicketAmount:
                        +purgedForm.averagePrivateEquityTicketAmount || undefined,
                    professionalClientPercentage:
                        +purgedForm.professionalClientPercentage || undefined,
                    informedClientPercentage: +purgedForm.informedClientPercentage || undefined,
                    nonProfessionalClientPercentage:
                        +purgedForm.nonProfessionalClientPercentage || undefined,
                },
                callback: () => setForm(getDefaultForm()),
            });
        }
    }, [form, distributor, updateDistributorCompany]);

    const hasMissingInfo =
        !distributor?.assetManagementAmount ||
        !distributor?.privateEquityAssetManagementPercentage ||
        !hasClientRepartition ||
        !distributor?.distributionWays ||
        !distributor?.averagePrivateEquityTicketAmount;

    return (
        <InfoBox title={texts.COMPLEMENTS}>
            <InfoRow
                title={texts.ASSET_MANAGEMENT_AMOUNT_LABEL}
                data={assetManagementAmount}
                mb={assetManagementAmount === '-' && canEditMissingInfo ? 2 : 0}
            >
                {canEditMissingInfo && (
                    <RadioGroup
                        name="assetManagementAmount"
                        value={form.assetManagementAmount}
                        onChange={handleChange('assetManagementAmount')}
                    >
                        {radioKeys.map(key => (
                            <RadioLabel key={key} value={key} label={messages?.[key]} />
                        ))}
                    </RadioGroup>
                )}
            </InfoRow>
            <InfoRow
                title={texts.PRIVATE_EQUITY_ASSET_MANAGEMENT_AMOUNT_LABEL}
                data={
                    distributor?.privateEquityAssetManagementPercentage
                        ? distributor?.privateEquityAssetManagementPercentage + '%'
                        : '-'
                }
            >
                {canEditMissingInfo && (
                    <CustomTextField
                        formatNumber
                        sx={inputSx}
                        value={form.privateEquityAssetManagementPercentage}
                        error={errors.privateEquityAssetManagementPercentage}
                        showError={!!errors.privateEquityAssetManagementPercentage}
                        onChange={handleChange('privateEquityAssetManagementPercentage')}
                        endAdornment={<InputAdornment position="start">%</InputAdornment>}
                    />
                )}
            </InfoRow>
            <InfoRow
                title={texts.CLIENT_REPARTITION_LABEL}
                data={
                    hasClientRepartition ? (
                        <React.Fragment>
                            {clientRepartition.map((repartition, index) => (
                                <Typography key={index}>{repartition}</Typography>
                            ))}
                        </React.Fragment>
                    ) : (
                        '-'
                    )
                }
            >
                {canEditMissingInfo && (
                    <React.Fragment>
                        <FormControlLabel
                            sx={formControlSx}
                            control={
                                <CustomTextField
                                    formatNumber
                                    sx={percentageInputSx}
                                    formControlProps={{ sx: { marginBottom: 0 } }}
                                    value={form.professionalClientPercentage}
                                    onChange={handleChange('professionalClientPercentage')}
                                    error={errors.professionalClientPercentage}
                                    showError={!!errors.professionalClientPercentage}
                                    endAdornment={
                                        <InputAdornment position="start">%</InputAdornment>
                                    }
                                />
                            }
                            label={
                                <Box display="flex" alignItems="center" flexWrap="nowrap" ml={1}>
                                    {texts.PRO_CLIENTS_PERCENTAGE}
                                </Box>
                            }
                        />
                        <FormControlLabel
                            sx={formControlSx}
                            control={
                                <CustomTextField
                                    formatNumber
                                    sx={percentageInputSx}
                                    formControlProps={{ sx: { marginBottom: 0 } }}
                                    value={form.informedClientPercentage}
                                    onChange={handleChange('informedClientPercentage')}
                                    error={errors.informedClientPercentage}
                                    showError={!!errors.informedClientPercentage}
                                    endAdornment={
                                        <InputAdornment position="start">%</InputAdornment>
                                    }
                                />
                            }
                            label={
                                <Box display="flex" alignItems="center" flexWrap="nowrap" ml={1}>
                                    {texts.INFORMED_CLIENTS_PERCENTAGE}
                                </Box>
                            }
                        />
                        <FormControlLabel
                            sx={formControlSx}
                            control={
                                <CustomTextField
                                    formatNumber
                                    sx={percentageInputSx}
                                    formControlProps={{ sx: { marginBottom: 0 } }}
                                    value={form.nonProfessionalClientPercentage}
                                    onChange={handleChange('nonProfessionalClientPercentage')}
                                    error={errors.informedClientPercentage}
                                    showError={!!errors.informedClientPercentage}
                                    endAdornment={
                                        <InputAdornment position="start">%</InputAdornment>
                                    }
                                />
                            }
                            label={
                                <Box display="flex" alignItems="center" flexWrap="nowrap" ml={1}>
                                    {texts.NON_PRO_CLIENTS_PERCENTAGE}
                                </Box>
                            }
                        />
                        {errors.totalPercent && <ErrorMessage message={errors.totalPercent} />}
                    </React.Fragment>
                )}
            </InfoRow>
            <InfoRow
                title={texts.DISTRIBUTION_WAYS_LABEL}
                data={distributor?.distributionWays || '-'}
            >
                {canEditMissingInfo && (
                    <CustomTextField
                        multiline
                        rows={3}
                        sx={theme => ({ ...inputSx, height: 'auto', padding: 0 })}
                        value={form.distributionWays}
                        onChange={handleChange('distributionWays')}
                        error={errors.distributionWays}
                        showError={!!errors.distributionWays}
                    />
                )}
            </InfoRow>
            <InfoRow
                title={texts.AVERAGE_PRIVATE_EQUITY_TICKET_AMOUNT_LABEL}
                data={
                    distributor?.averagePrivateEquityTicketAmount
                        ? parseSum(distributor?.averagePrivateEquityTicketAmount)
                        : '-'
                }
            >
                {canEditMissingInfo && (
                    <CustomTextField
                        formatNumber
                        sx={inputSx}
                        value={form.averagePrivateEquityTicketAmount}
                        onChange={handleChange('averagePrivateEquityTicketAmount')}
                        error={errors.averagePrivateEquityTicketAmount}
                        showError={!!errors.averagePrivateEquityTicketAmount}
                        endAdornment={
                            <InputAdornment position="start">{EURO_SYMBOL}</InputAdornment>
                        }
                    />
                )}
            </InfoRow>
            <InfoRow title={texts.REMUNERATION_METHOD_LABEL} data={remunerationMethod} />
            {hasMissingInfo && canEditMissingInfo && (
                <SectionActions
                    loading={loading}
                    handleCancel={handleCancel}
                    handleSubmit={handleSubmit}
                />
            )}
        </InfoBox>
    );
};

export default ComplementsSesction;
